/**
 * Unsent inline edits for the Action View (HIG-5830). A field is present only
 * once the user has touched it — its value then overlays the server draft
 * until it is persisted or discarded. Keyed by action id at module scope so a
 * regeneration landing mid-edit can never silently clobber them. Leaving the
 * view resolves the draft through the leave guard's save/discard dialog;
 * hiding the panel keeps it, so reopening restores the edit.
 *
 * The two fields commit differently: a title saves itself when the field is
 * done (blur, Enter, leaving the view), so only a body edit ever reaches the
 * Save footer.
 */
export interface ActionInlineDraft {
  /** Edited core title (the editable region of the display title). */
  title?: string
  /** Edited markdown body. */
  content?: string
  /**
   * Set when a title's own commit failed, which hands the staged title to the
   * Save footer to retry — a blur that failed silently would lose the edit.
   */
  titleError?: string
  /**
   * Server-content snapshot from when editing began. A mismatch against the
   * live content means a scoped-chat regeneration landed under the draft —
   * the footer warns that Save will keep the user's version.
   */
  base?: { content: string | undefined }
}

/** Any staged edit at all — what keeps a draft entry worth holding on to. */
export function hasDraftEdits(draft: ActionInlineDraft | undefined): boolean {
  return draft?.title !== undefined || draft?.content !== undefined
}

/**
 * Tiptap's markdown round-trip (and StarterKit's trailing paragraph) often
 * differs from the server string by trailing newlines only. Those are not a
 * user edit — Save / the leave dialog must not open for them.
 */
export function isSameDraftContent(a: string | undefined, b: string | undefined): boolean {
  return (a ?? '').replace(/\n+$/, '') === (b ?? '').replace(/\n+$/, '')
}

/**
 * Whether the view owes an explicit Save. Body edits do: markdown is a document
 * the user is composing, and the footer is where they commit it. A title
 * doesn't — it saves on blur — so it only lands here when its own commit failed
 * and Save is the retry. Content that still matches the snapshot it was staged
 * from is not a change (focus / editor settle must not take the footer).
 */
export function requiresExplicitSave(draft: ActionInlineDraft | undefined): boolean {
  if (draft?.titleError !== undefined) return true
  if (draft?.content === undefined) return false
  if (draft.base && isSameDraftContent(draft.content, draft.base.content)) return false
  return true
}

/**
 * True when a regeneration rewrote the server draft underneath an unsent body
 * edit. Content that matches the user's own draft is no conflict — that's what
 * Save's optimistic update writes, and a regen landing on the same text has
 * nothing to clobber. Title-only drafts are never in conflict: they don't wait
 * on the footer, and Save wouldn't be sending content anyway.
 */
export function isDraftSuperseded(draft: ActionInlineDraft | undefined, currentContent: string | undefined): boolean {
  return (
    draft?.content !== undefined &&
    !!draft.base &&
    draft.base.content !== currentContent &&
    draft.content !== currentContent
  )
}

/**
 * The title a commit should send: trimmed, and null when there's nothing worth
 * persisting (untouched, blank, or back to the saved title). Shared by the
 * blur-commit and the footer's Save so both answer this the same way.
 */
export function resolveSavableTitle(title: string | undefined, coreTitle: string | undefined): string | null {
  if (title === undefined) return null
  const trimmed = title.trim()
  if (!trimmed || trimmed === coreTitle) return null
  return trimmed
}

type Drafts = Record<string, ActionInlineDraft>

function withEntry(drafts: Drafts, actionId: string, entry: ActionInlineDraft): Drafts {
  // An entry holding no edited field is just its base snapshot — drop it so
  // the dirty gate releases (e.g. the title was typed back to its original).
  if (!hasDraftEdits(entry)) return discardDraft(drafts, actionId)
  return { ...drafts, [actionId]: entry }
}

/**
 * Stage a title keystroke. Typing the field back to the pristine core clears
 * it, so an untouched-in-effect draft doesn't hold a commit open. Typing also
 * supersedes a previous commit failure — this text is what will be sent next.
 */
export function stageDraftTitle(
  drafts: Drafts,
  actionId: string,
  title: string,
  current: { coreTitle: string; content: string | undefined },
): Drafts {
  const existing = drafts[actionId] ?? { base: { content: current.content } }
  const { titleError: _stale, ...entry } = existing
  if (title === current.coreTitle) {
    const { title: _cleared, ...rest } = entry
    return withEntry(drafts, actionId, rest)
  }
  return { ...drafts, [actionId]: { ...entry, title } }
}

/**
 * Stage a body edit, capturing the base snapshot on first touch. Backfills a
 * missing base too: a title kept across a leave (discardDraftKeepingTitle)
 * has none, and its stale snapshot wouldn't describe when *this* edit began.
 * Typing the field back to the saved body (or an editor settle that only
 * adds trailing newlines) clears it, same as the title field.
 */
export function stageDraftContent(
  drafts: Drafts,
  actionId: string,
  content: string,
  current: { content: string | undefined },
): Drafts {
  const entry = drafts[actionId] ?? {}
  if (isSameDraftContent(content, current.content)) {
    const { content: _cleared, ...rest } = entry
    return withEntry(drafts, actionId, rest)
  }
  const base = entry.base ?? { content: current.content }
  return { ...drafts, [actionId]: { ...entry, base, content } }
}

/**
 * Let go of the title field — Escape reverting it, or a commit that persisted
 * it. Keeps any body edit.
 */
export function clearDraftTitle(drafts: Drafts, actionId: string): Drafts {
  const entry = drafts[actionId]
  if (!entry || (entry.title === undefined && entry.titleError === undefined)) return drafts
  const { title: _cleared, titleError: _clearedError, ...rest } = entry
  return withEntry(drafts, actionId, rest)
}

/**
 * Release a title the commit just persisted. Scoped to the value that was sent:
 * the user can blur, refocus and type again while the PATCH is in flight, and
 * that newer text is still unsaved.
 */
export function clearDraftTitleIfUnchanged(drafts: Drafts, actionId: string, savedTitle: string): Drafts {
  const entry = drafts[actionId]
  if (!entry || entry.title === undefined || entry.title.trim() !== savedTitle) return drafts
  return clearDraftTitle(drafts, actionId)
}

/**
 * Release only the body value captured by a completed save. A title edit, or
 * body text typed while that request was in flight, remains staged.
 */
export function clearDraftContentIfUnchanged(drafts: Drafts, actionId: string, savedContent: string): Drafts {
  const entry = drafts[actionId]
  if (!entry || entry.content !== savedContent) return drafts
  const { content: _cleared, ...rest } = entry
  return withEntry(drafts, actionId, rest)
}

/**
 * Record that a title commit failed, keeping the typed value so the Save footer
 * can retry it. A no-op once the title is gone (reverted, or already saved by a
 * later commit) — there's nothing left to retry.
 */
export function markDraftTitleError(drafts: Drafts, actionId: string, message: string): Drafts {
  const entry = drafts[actionId]
  if (!entry || entry.title === undefined) return drafts
  return { ...drafts, [actionId]: { ...entry, titleError: message } }
}

export function discardDraft(drafts: Drafts, actionId: string): Drafts {
  if (!(actionId in drafts)) return drafts
  const { [actionId]: _dropped, ...rest } = drafts
  return rest
}

/**
 * Leave-time discard (the body's unmount cleanup). Backing out cancels the
 * body edit outright, but a staged title is not the leave's to drop: the
 * header's unmount commit saves it, and that commit re-reads this atom after
 * any in-flight PATCH settles — discarding here would lose a title retyped
 * during that PATCH. The commit releases the title itself once it's persisted
 * (clearDraftTitleIfUnchanged).
 */
export function discardDraftKeepingTitle(drafts: Drafts, actionId: string): Drafts {
  const entry = drafts[actionId]
  if (!entry) return drafts
  if (entry.title === undefined) return discardDraft(drafts, actionId)
  return { ...drafts, [actionId]: { title: entry.title } }
}

export interface SplitTitle {
  prefix: string
  core: string
  suffix: string
}

/**
 * Split a display title around its editable core (`editOptions.title.coreTitle`
 * — the underlying entity title, which the display title may decorate, e.g.
 * "Create Linear issue: <core>"). Returns null when the core isn't a substring
 * of the display title, in which case inline title editing has no anchor.
 */
export function splitEditableTitle(title: string, coreTitle: string): SplitTitle | null {
  if (!coreTitle) return null
  const index = title.indexOf(coreTitle)
  if (index === -1) return null
  return {
    prefix: title.slice(0, index),
    core: coreTitle,
    suffix: title.slice(index + coreTitle.length),
  }
}
